'use client';

import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ComparableParameterGroup } from '../../../schemas/ComparableParameterGroup';
import {
    getParameterFormattedValue,
    getParameterFormattedValueByParameterId,
} from '../../../utils/ParameterRelatedFunction';
import { CS_BASE_URL, NEW_CS_BASE_URL } from '../../../utils/ProjectUrls';
import { LanguageMapping } from '../../../utils/LanguageMapping';
import { useLocale } from '../../../contexts/LocaleProvider';
import { DisplayName } from '../../../schemas/DisplayName';
import { CompareItem } from './CompareBar';
import { handleIconClick } from '../singleProductPageComponents/ProductInfoDetail';
import { ReactComponent as SpecSheetIcon } from '../../../static/product_image/specsheetIcon.svg';
import { ReactComponent as DataSheetIcon } from '../../../static/product_image/datasheetIcon.svg';
import { OctoPartResponse } from '../singleProductPageComponents/Distributors';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { Link, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { formatCountIcuString } from '../../../utils/formatCount';

interface CompareInfoTableProps {
    /** 以料號為 key 的 Map，value 是該料號對應的參數陣列 */
    partParamMap: Map<string, ComparableParameterGroup[]>;
    /** 每個料號的詳細資訊 (如 name、imagePath) */
    compareItems: CompareItem[];
    onClearCompareItem: (basePn: string) => void;
    contentTree: ProductsContent;
}

const iconStyle = {
    marginRight: '15px',
    cursor: 'pointer',
    '&:hover path': {
        fill: '#E5673E',
    },
    display: 'flex',
    alignItems: 'center',
};

const getPartNumber = (item: CompareItem): string => {
    if ('displayPn' in item && item.displayPn) {
        return item.displayPn;
    }
    if ('basePn' in item) {
        return item.basePn as string;
    }
    if ('uniquePn' in item) {
        return item.uniquePn;
    }
    return '';
};

const getImagePath = (item: CompareItem): string => {
    if ('imagePath' in item && typeof item.imagePath === 'string' && item.imagePath) {
        return `https://search.kemet.com/component-images/${item.imagePath}`;
    }
    if ('imageUrl' in item && typeof item.imageUrl === 'string' && item.imageUrl) {
        return item.imageUrl;
    }
    return 'https://search.kemet.com/component-images/ceramic_chip_standard.jpg';
};

/**
 * CompareInfoTable 負責將 Map 結構轉為二維陣列，並顯示比較參數。
 */
export async function fetchStockForPart(partNumber: string): Promise<number> {
    const requestBody = { uniquePn: partNumber };
    try {
        const response = await fetch(`${CS_BASE_URL}/octopart/parts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        });
        const partsData: OctoPartResponse = await response.json();
        // 假設取第一筆結果的 quantity
        return partsData.octoParts.reduce((acc, part) => acc + part.quantity, 0);
    } catch (error) {
        console.error('Failed to fetch stock for', partNumber, error);
        return 0;
    }
}
const CompareInfoTable: FunctionComponent<CompareInfoTableProps> = ({
    partParamMap,
    compareItems,
    onClearCompareItem,
    contentTree,
}) => {
    const { locale } = useLocale();
    const language = LanguageMapping.get(locale) || 'ENGLISH';
    const location = useLocation();

    const getDisplayName = (paramObj: ComparableParameterGroup | undefined): string => {
        if (!paramObj) return '';
        const englishName = paramObj.displayNames?.find(
            (dn: DisplayName) => dn.language === language
        );
        return englishName?.display || paramObj.name || '';
    };

    // 取得所有料號 (keys)，並用 useMemo 確保每次 render 時順序一致
    const partNumbers = useMemo(() => Array.from(partParamMap.keys()), [partParamMap]);

    // 建立 state 來存放每個料號的庫存量
    const [stockMap, setStockMap] = useState<Record<string, number>>({});

    // 當 partNumbers 改變時，依序 fetch 每個料號的庫存量
    useEffect(() => {
        async function fetchAllStocks() {
            const newStockMap: Record<string, number> = {};
            await Promise.all(
                partNumbers.map(async (pn) => {
                    const qty = await fetchStockForPart(pn);
                    newStockMap[pn] = qty;
                })
            );
            setStockMap(newStockMap);
        }

        if (partNumbers.length > 0) {
            fetchAllStocks();
        }
    }, [partNumbers]);

    // 依照 partNumbers 的順序組出二維參數陣列
    const comparableParameterGroups = useMemo(() => {
        return partNumbers.map((pn) => partParamMap.get(pn) ?? []);
    }, [partParamMap, partNumbers]);

    // 取得所有參數的 id（去重）
    const allParamIds = useMemo(() => {
        const ids = new Set<number>();
        comparableParameterGroups.forEach((partParams) => {
            partParams.forEach((param) => ids.add(param.id));
        });
        return Array.from(ids);
    }, [comparableParameterGroups]);

    // 取一個變數判斷是否有資料
    const hasData = comparableParameterGroups.length > 0;

    const {
        isMaxScreen,
        isScreenXxl,
        isScreenXl,
        isScreenMd,
        isCustomScreenXxxl,
        isCustomScreenXxl,
    } = useResponsive();

    // 固定最多 3 欄；左側 1 欄放參數名稱，右側生成 3 欄
    const itemCount = 3;

    const firstColumnWidth = (() => {
        if (isMaxScreen) return '350px';
        if (isCustomScreenXxxl) return '290px';
        if (isCustomScreenXxl) return '265px';
        if (isScreenXxl) return '215px';
        if (isScreenXl) return '190px';
        else return '160px';
    })();

    const gridTemplate = `${firstColumnWidth} repeat(${itemCount}, 1fr)`;

    const detailPadding = (() => {
        if (isScreenMd) return '10px 20px';
        return '10px 20px';
    })();

    const dynamicGap = (() => {
        if (isMaxScreen || isCustomScreenXxxl || isCustomScreenXxl) return '2rem';
        else return '1rem';
    })();

    return (
        <>
            {hasData ? (
                <Box
                    data-testid="compare-info-table"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        overflowY: 'auto',
                        paddingBottom: '1rem',
                    }}
                >
                    {/* 表格最上方：顯示每個料號的名稱與圖片 */}
                    <Box
                        data-testid="compare-items-header-row"
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: gridTemplate,
                            width: '100%',
                            padding: '0',
                            gap: dynamicGap, // 加上 gap 與下方 param row 一致
                            backgroundColor: 'white !important', // 強制表頭為白底
                            paddingBottom: '5px',
                        }}
                    >
                        {/* 左側參數欄標頭 */}
                        <Box
                            data-testid="compare-param-header"
                            sx={{ fontWeight: 'bold', textAlign: 'center' }}
                        />

                        {/* 右側：各料號頭部資訊（名稱、圖片），固定 3 欄 */}
                        {Array.from({ length: itemCount }, (_, idx) => {
                            const item = compareItems[idx];
                            return (
                                <Box
                                    data-testid={`compare-item-header-${idx}`}
                                    key={`header-${idx}`}
                                    sx={{
                                        position: 'relative', // 讓 IconButton 能絕對定位
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: '0',
                                        backgroundColor: 'white !important',
                                        justifyContent: 'center', // 新增：垂直置中
                                        textAlign: 'center', // 新增：文字置中
                                    }}
                                >
                                    {item && (
                                        <IconButton
                                            onClick={() => onClearCompareItem(getPartNumber(item))}
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                width: '24px',
                                                height: '24px',
                                                padding: 0,
                                                backgroundColor: '#B75332',
                                                borderRadius: '50%',
                                                transition: 'none',
                                            }}
                                        >
                                            <CloseIcon fontSize="small" sx={{ color: 'white' }} />
                                        </IconButton>
                                    )}
                                    {item ? (
                                        <>
                                            <img
                                                data-testid={`compare-item-image-${idx}`}
                                                src={getImagePath(item)}
                                                alt={getPartNumber(item)}
                                                style={{
                                                    width: '85px',
                                                    height: '85px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                            <Box
                                                data-testid={`compare-item-name-${idx}`}
                                                sx={{
                                                    fontFamily: 'Poppins',
                                                    fontWeight: 400,
                                                    fontSize: '20px',
                                                    lineHeight: '117%',
                                                    letterSpacing: '0%',
                                                    textAlign: 'center',
                                                    color: '#B75332',
                                                    marginTop: '4px',
                                                    paddingBottom: '5px',
                                                }}
                                            >
                                                {getPartNumber(item)}
                                            </Box>
                                            <Typography
                                                data-testid="stock"
                                                sx={{
                                                    fontFamily: 'Poppins',
                                                    fontWeight: 400,
                                                    fontSize: '12px',
                                                    lineHeight: '117%',
                                                    color: '#0E8A13',
                                                    paddingBottom: '5px',
                                                }}
                                            >
                                                {formatCountIcuString(
                                                    contentTree.filters.labels.resultCountCompact
                                                        .content,
                                                    stockMap[getPartNumber(item)] || 0,
                                                    locale
                                                )}
                                            </Typography>

                                            {/* 將 Specsheet 與 Datasheet icon 水平排列 */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    gap: 0,
                                                    paddingBottom: '5px',
                                                }}
                                            >
                                                {('uniquePn' in item
                                                    ? getParameterFormattedValue(
                                                          item.parameterValues,
                                                          'Specsheet Suppression'
                                                      ) !== 'true'
                                                    : item.specsheetSuppression !== true) && (
                                                    <Box
                                                        sx={iconStyle}
                                                        onClick={() =>
                                                            handleIconClick(
                                                                `${NEW_CS_BASE_URL}/component-documentation/download/specsheet/${
                                                                    'uniquePn' in item
                                                                        ? encodeURIComponent(
                                                                              item.uniquePn as string
                                                                          )
                                                                        : encodeURIComponent(
                                                                              item.basePn
                                                                          )
                                                                }?lang=${locale}`
                                                            )
                                                        }
                                                    >
                                                        <SpecSheetIcon title="SpecSheet" />
                                                        <Typography
                                                            sx={{
                                                                fontFamily: 'Poppins',
                                                                fontWeight: 400,
                                                                fontSize: '12px',
                                                                lineHeight: '100%',
                                                                letterSpacing: '0%',
                                                                verticalAlign: 'bottom',
                                                                marginLeft: '4px', // 若需要小間距分隔圖示與文字
                                                            }}
                                                        >
                                                            {contentTree.product.specsheet.content}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {('uniquePn' in item
                                                    ? getParameterFormattedValueByParameterId(
                                                          item.parameterValues,
                                                          296
                                                      )
                                                    : item.dataSheet) && (
                                                    <Box
                                                        sx={iconStyle}
                                                        onClick={() =>
                                                            handleIconClick(
                                                                `${NEW_CS_BASE_URL}/component-documentation/download/datasheet/${
                                                                    'uniquePn' in item
                                                                        ? encodeURIComponent(
                                                                            item.uniquePn as string
                                                                        )
                                                                        : encodeURIComponent(
                                                                            item.basePn
                                                                        )
                                                                }?lang=${locale}`
                                                            )
                                                        }
                                                    >
                                                        <DataSheetIcon title="DataSheet" />
                                                        <Typography
                                                            sx={{
                                                                fontFamily: 'Poppins',
                                                                fontWeight: 400,
                                                                fontSize: '12px',
                                                                lineHeight: '100%',
                                                                letterSpacing: '0%',
                                                                verticalAlign: 'bottom',
                                                                marginLeft: '4px', // 若需要小間距分隔圖示與文字
                                                            }}
                                                        >
                                                            {contentTree.product.datasheet.content}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box
                                                sx={{
                                                    backgroundColor: '#B75332',
                                                    borderRadius: '5px',
                                                    padding: detailPadding,
                                                    cursor: 'pointer',
                                                    width: '163px',
                                                    height: '24px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    transition: 'none',
                                                    '&:hover': {
                                                        transition: 'none',
                                                        background:
                                                            'linear-gradient(0deg, #B75332 0%, #EE9769 100%)',
                                                    },
                                                }}
                                                component={Link}
                                                to={`${location.pathname}/part/${encodeURIComponent(getPartNumber(item))}`}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Poppins',
                                                        fontWeight: 600,
                                                        fontSize: '16px',
                                                        lineHeight: '24px',
                                                        color: '#FFFFFF',
                                                    }}
                                                >
                                                    {
                                                        contentTree.product
                                                            .productDetailsButtonLabel.content
                                                    }
                                                </Typography>
                                            </Box>
                                        </>
                                    ) : (
                                        <Box
                                            sx={{ height: '70px', width: '100%' }}
                                            data-testid={`compare-item-header-empty-${idx}`}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* 每個參數列 */}
                    {allParamIds.map((paramId) => {
                        // 找出該參數在每個料號中的物件
                        const paramObjs = comparableParameterGroups.map((partParams) =>
                            partParams.find((p) => p.id === paramId)
                        );
                        function findFirstTruthy<T>(arr: T[]): T | undefined {
                            return arr.find(Boolean);
                        }
                        // 第一個料號作為基準取得參數名稱
                        const paramName = getDisplayName(findFirstTruthy(paramObjs));

                        // 收集該參數在各料號的值
                        const values = paramObjs.map((po) => po?.value?.formattedValue || '');

                        // 檢查是否全部值都一樣，如果不一樣就要整列高亮
                        const allSame = values.every((v) => v === values[0]);
                        const rowBgColor = allSame ? 'transparent' : '#EE976980';

                        return (
                            <Box
                                data-testid="compare-param-row"
                                key={paramId}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: gridTemplate,
                                    width: '100%',
                                    gap: dynamicGap,
                                    padding: '0',
                                    backgroundColor: rowBgColor,
                                }}
                            >
                                {/* 左側：參數名稱 */}
                                <Box
                                    data-testid="compare-param-name"
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '117%',
                                        letterSpacing: '0%',
                                        padding: '5px 1rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {paramName}
                                </Box>

                                {/* 右側：各料號的值（固定 3 欄） */}
                                {Array.from({ length: itemCount }, (_, idx) => {
                                    const val = values[idx] || '';
                                    return (
                                        <Box
                                            data-testid="compare-param-value"
                                            key={`val-${paramId}-${idx}`}
                                            sx={{
                                                flex: 1,
                                                padding: '0 1rem 0 1rem',
                                                backgroundColor: '#F2F2F280', // 灰色半透明背景
                                                display: 'flex',
                                                alignItems: 'center',
                                                backdropFilter: 'blur(2px)',
                                                fontFamily: 'Poppins',
                                                fontWeight: 400,
                                                fontSize: '16px',
                                                lineHeight: '117%',
                                            }}
                                        >
                                            {val}
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Box>
            ) : null}
        </>
    );
};

export default CompareInfoTable;
